import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProductService } from '../../services/product.service';
import { SearchService } from '../../services/search.service';
import { Product, ProductFilters, SortOption } from '../../models/product.model';
import { ProductListComponent } from '../../components/product-list/product-list.component';
import { FiltersSidebarComponent } from '../../components/filters-sidebar/filters-sidebar.component';

@Component({
  selector: 'app-products',
  imports: [CommonModule, FormsModule, ProductListComponent, FiltersSidebarComponent],
  templateUrl: './products.component.html',
  styleUrl: './products.component.scss'
})
export class ProductsComponent implements OnInit {
  protected allProducts = signal<Product[]>([]);
  protected filteredProducts = signal<Product[]>([]);
  protected displayedProducts = signal<Product[]>([]);
  protected isLoading = signal(true);
  protected currentPage = signal(1);
  protected sortBy = signal<SortOption>('relevancia');
  protected searchQuery = signal('');
  protected filters: ProductFilters = {};

  constructor(
    private productService: ProductService,
    private searchService: SearchService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadProducts();
    
    // Listen to route params for category
    this.route.params.subscribe(params => {
      const categorySlug = params['slug'];
      if (categorySlug) {
        this.applyCategoryFilter(categorySlug);
      }
    });
    
    // Listen to query params for search
    this.route.queryParams.subscribe(params => {
      const query = params['q'] || '';
      this.searchQuery.set(query);
      
      if (query) {
        this.performSearch(query);
      } else if (!this.route.snapshot.params['slug']) {
        this.applyFiltersAndSort();
      }
    });
  }

  applyCategoryFilter(slug: string): void {
    this.productService.getCategories().subscribe(categories => {
      const category = categories.find(c => c.slug === slug);
      if (category) {
        this.filters.categoriaId = category.id;
        this.applyFiltersAndSort();
      }
    });
  }

  loadProducts(): void {
    this.isLoading.set(true);
    
    this.productService.getProducts().subscribe(products => {
      this.allProducts.set(products);
      this.searchService.initializeSearch(products);
      this.applyFiltersAndSort();
      this.isLoading.set(false);
    });
  }

  performSearch(query: string): void {
    if (!query || query.trim().length < 2) {
      this.filteredProducts.set(this.allProducts());
      this.applyFiltersAndSort();
      return;
    }

    const results = this.searchService.intelligentSearch(query);
    const products = results.map(r => r.product);
    this.filteredProducts.set(products);
    this.applySortAndUpdate();
  }

  onFiltersChange(filters: ProductFilters): void {
    this.filters = filters;
    this.currentPage.set(1);
    
    if (this.searchQuery()) {
      // Se há busca ativa, aplica filtros nos resultados da busca
      const searchResults = this.searchService.intelligentSearch(this.searchQuery());
      let products = searchResults.map(r => r.product);
      products = this.productService.filterProducts(products, filters);
      this.filteredProducts.set(products);
    } else {
      // Senão, aplica filtros em todos os produtos
      const filtered = this.productService.filterProducts(this.allProducts(), filters);
      this.filteredProducts.set(filtered);
    }
    
    this.applySortAndUpdate();
  }

  onSortChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const sort = target.value as SortOption;
    this.sortBy.set(sort);
    this.applySortAndUpdate();
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
  }

  private applyFiltersAndSort(): void {
    const filtered = this.productService.filterProducts(this.allProducts(), this.filters);
    this.filteredProducts.set(filtered);
    this.applySortAndUpdate();
  }

  private applySortAndUpdate(): void {
    const sorted = this.productService.sortProducts(this.filteredProducts(), this.sortBy());
    this.displayedProducts.set(sorted);
  }

  get resultCount(): number {
    return this.displayedProducts().length;
  }

  get hasSearch(): boolean {
    return this.searchQuery().length > 0;
  }
}

